import React, { useEffect, useMemo, useState } from "react";
import api, { downloadFile } from "../api/client";
import { AuditEntry, AuditLogPage, Customer } from "../types";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import { useBranding } from "../context/BrandingContext";
import { formatDateTime } from "../utils/datetime";
import { useVisibleColumnCount } from "../hooks/useVisibleColumns";
import ActionButton from "../components/ActionButton";

const RESULT_COLORS: Record<string, string> = {
  success: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  failure: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  failed: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  pending: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
};

const PAGE_SIZE = 50;

export default function AuditLog() {
  // Audit timestamps must honour Settings -> System -> Display Time Zone. The
  // API sends zone-less UTC, so a bare new Date() rendered every entry shifted
  // by the viewer's offset and ignored the configured zone entirely.
  const { display_timezone: tz } = useBranding();
  // Filters (URL-encoded as query params on every fetch). Kept as
  // strings so empty == "no filter"; converted at request time.
  const [action, setAction] = useState("");
  const [actor, setActor] = useState("");
  const [resultFilter, setResultFilter] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [sinceDays, setSinceDays] = useState("");
  const [offset, setOffset] = useState(0);

  const [actions, setActions] = useState<string[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [page, setPage] = useState<AuditLogPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);
  // colSpan is an ATTRIBUTE, so no media query reaches it: with three
  // columns hidden, a colSpan={6} cell is still measured at full width and
  // holds the table open by itself. Order MUST mirror the header list.
  const auditCols = useVisibleColumnCount(["sm", "sm", "base", "lg", "md", "base"]);
  const sort = useSort("created_at", "desc");

  // Loaders for filter dropdowns (one-time)
  useEffect(() => {
    api
      .get("/api/v1/audit-logs/actions")
      .then((r) => setActions(r.data))
      .catch(() => {});
    api
      .get("/api/v1/customers")
      .then((r) => setCustomers(r.data))
      .catch(() => {});
  }, []);

  const params = useMemo(() => {
    const p: Record<string, string | number> = { limit: PAGE_SIZE, offset };
    if (action) p.action = action;
    if (actor.trim()) p.actor = actor.trim();
    if (resultFilter) p.result = resultFilter;
    if (customerId) p.customer_id = customerId;
    if (sinceDays) {
      const days = parseInt(sinceDays, 10);
      if (Number.isFinite(days) && days > 0) {
        p.since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
      }
    }
    return p;
  }, [action, actor, resultFilter, customerId, sinceDays, offset]);

  useEffect(() => {
    setLoading(true);
    api
      .get("/api/v1/audit-logs", { params })
      .then((r) => setPage(r.data))
      .catch(() => setPage(null))
      .finally(() => setLoading(false));
  }, [params]);

  // Reset to page 0 when filters change
  const resetOffsetAnd =
    <T,>(setter: React.Dispatch<React.SetStateAction<T>>) =>
    (v: T) => {
      setOffset(0);
      setter(v);
    };

  const downloadCsv = async () => {
    // Strip pagination params from the CSV request
    const { limit: _l, offset: _o, ...csvParams } = params;
    void _l;
    void _o;
    await downloadFile("/api/v1/audit-logs/export.csv", "audit_log.csv", csvParams);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Audit Log</h1>
        <ActionButton onAction={() => downloadCsv()} variant="raw" className="border dark:border-gray-600 text-sm px-4 py-1.5 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40" pendingLabel="Preparing…">
          Download CSV
        </ActionButton>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 mb-4 flex flex-wrap gap-3 items-end">
        {/* min-w-0 so these can actually shrink: a flex child defaults to its
            content's min-content width and refuses to go narrower, which is
            what let the Action select push the card past the screen edge. */}
        <div className="min-w-0 flex-1 sm:flex-none">
          <label className="text-xs uppercase text-gray-500 dark:text-gray-400 block mb-1">Action</label>
          <select
            value={action}
            onChange={(e) => resetOffsetAnd(setAction)(e.target.value)}
            className="border rounded-lg px-3 py-1.5 text-sm w-full max-w-full sm:w-auto sm:min-w-[180px] dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
          >
            <option value="">All</option>
            {actions.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase text-gray-500 dark:text-gray-400 block mb-1">
            Actor (exact username)
          </label>
          <input
            value={actor}
            onChange={(e) => resetOffsetAnd(setActor)(e.target.value)}
            placeholder="alice"
            className="border rounded-lg px-3 py-1.5 text-sm w-40 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
          />
        </div>
        <div>
          <label className="text-xs uppercase text-gray-500 dark:text-gray-400 block mb-1">Customer</label>
          <select
            value={customerId}
            onChange={(e) => resetOffsetAnd(setCustomerId)(e.target.value)}
            className="border rounded-lg px-3 py-1.5 text-sm w-full max-w-full sm:w-auto sm:min-w-[180px] dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
          >
            <option value="">All</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase text-gray-500 dark:text-gray-400 block mb-1">Result</label>
          <select
            value={resultFilter}
            onChange={(e) => resetOffsetAnd(setResultFilter)(e.target.value)}
            className="border rounded-lg px-3 py-1.5 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
          >
            <option value="">All</option>
            <option value="success">Success</option>
            <option value="failure">Failure</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
          </select>
        </div>
        <div>
          <label className="text-xs uppercase text-gray-500 dark:text-gray-400 block mb-1">Last N days</label>
          <input
            type="number"
            min={1}
            value={sinceDays}
            onChange={(e) => resetOffsetAnd(setSinceDays)(e.target.value)}
            placeholder="any time"
            className="border rounded-lg px-3 py-1.5 text-sm w-28 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
          />
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
        <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <div>
            {loading ? (
              "Loading…"
            ) : page ? (
              <>
                Showing {page.items.length} of <strong>{page.total}</strong> events
                {page.total > 0 && (
                  <>
                    {" "}
                    · page {Math.floor(page.offset / page.limit) + 1} of{" "}
                    {Math.max(1, Math.ceil(page.total / page.limit))}
                  </>
                )}
              </>
            ) : (
              "—"
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0}
              className="border dark:border-gray-600 rounded px-2 py-1 disabled:opacity-40"
            >
              ◀
            </button>
            <button
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={!page || offset + PAGE_SIZE >= page.total}
              className="border dark:border-gray-600 rounded px-2 py-1 disabled:opacity-40"
            >
              ▶
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm md:min-w-[640px]">
            <thead className="bg-gray-50 dark:bg-gray-900/50 text-xs uppercase text-gray-500 dark:text-gray-400">
              <tr>
                <SortableTh label="When" sortKey="created_at" sort={sort.sort} onSort={sort.toggle} hide="hidden sm:table-cell" />
                <SortableTh label="Actor" sortKey="actor_username" sort={sort.sort} onSort={sort.toggle} hide="hidden sm:table-cell" />
                <SortableTh label="Action" sortKey="action" sort={sort.sort} onSort={sort.toggle} />
                <SortableTh label="Target" sortKey="target" sort={sort.sort} onSort={sort.toggle} hide="hidden lg:table-cell" />
                <SortableTh label="Result" sortKey="result" sort={sort.sort} onSort={sort.toggle} hide="hidden md:table-cell" />
                <th className="text-left px-3 py-2 w-6" />
              </tr>
            </thead>
            <tbody>
              {page && page.items.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-gray-400 dark:text-gray-500 py-6">
                    No audit log entries match these filters.
                  </td>
                </tr>
              )}
              {/* Server-paginated: this sorts only the currently visible page. */}
              {sortRows(page?.items ?? [], sort.sort, (row, key) => {
                switch (key) {
                  case "actor_username":
                    return (row.actor_username || "").toLowerCase();
                  case "action":
                    return (row.action || "").toLowerCase();
                  case "target":
                    return String(row.target_label || row.target_id || "").toLowerCase();
                  case "result":
                    return (row.result || "").toLowerCase();
                  default:
                    return row.created_at ? Date.parse(row.created_at) : 0;
                }
              }).map((row) => (
                <React.Fragment key={row.id}>
                  <tr
                    onClick={() => setExpanded(expanded === row.id ? null : row.id)}
                    className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer"
                  >
                    <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 font-mono whitespace-nowrap hidden sm:table-cell">
                      {formatDateTime(row.created_at, tz)}
                    </td>
                    <td className="px-3 py-2 text-gray-800 dark:text-gray-200 hidden sm:table-cell">{row.actor_username || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {/* Capped, not wrapped: these keys are long and mono, and
                          a wrapped one makes every row three lines tall. Full
                          value on hover, and the row expands for the detail. */}
                      <div className="max-w-[9rem] sm:max-w-none truncate" title={row.action}>
                        {row.action}
                      </div>
                      {/* When and Actor are both hidden below sm, which left the
                          phone row saying what happened but not when or by
                          whom. */}
                      <div className="sm:hidden mt-0.5 font-sans text-[11px] text-gray-400 dark:text-gray-500 truncate">
                        {row.actor_username || "—"} · {formatDateTime(row.created_at, tz)}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300 hidden lg:table-cell">
                      {row.target_label || row.target_id || "—"}
                      {row.target_type && (
                        <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">({row.target_type})</span>
                      )}
                    </td>
                    <td className="px-3 py-2 hidden md:table-cell">
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${
                          RESULT_COLORS[row.result] ||
                          "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                        }`}
                      >
                        {row.result}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500">
                      {expanded === row.id ? "▾" : "▸"}
                    </td>
                  </tr>
                  {expanded === row.id && (
                    <tr className="bg-gray-50 dark:bg-gray-900/50">
                      <td colSpan={auditCols} className="px-3 py-3">
                        <ExpandedDetails row={row} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* Structured render of one audit-log row's details JSON.
 *
 * The details column is free-form JSON. Some writers store
 * ``{"before": {...}, "after": {...}}`` for update operations; others
 * store flat ``{"key": "value"}`` ad-hoc context. We try to detect
 * the diff shape and render it as a side-by-side table; otherwise
 * fall back to a key-value list. Power users can toggle the raw JSON
 * with the "View raw" button.
 */
function ExpandedDetails({ row }: { row: AuditEntry }) {
  const [showRaw, setShowRaw] = React.useState(false);
  const details = row.details as any;
  const hasDiff =
    details && typeof details === "object" && details.before && details.after;
  return (
    <div className="text-xs space-y-2">
      {row.customer_id && (
        <div>
          <span className="text-gray-500 dark:text-gray-400">Customer:</span> {row.customer_id}
        </div>
      )}
      {row.error_message && (
        <div className="text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded p-2">
          <span className="font-semibold">Error:</span> {row.error_message}
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="text-gray-500 dark:text-gray-400 font-semibold">Details</div>
        <button
          onClick={() => setShowRaw((v) => !v)}
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          {showRaw ? "Structured view" : "View raw JSON"}
        </button>
      </div>
      {showRaw ? (
        <pre className="font-mono text-xs bg-white dark:bg-gray-900 border dark:border-gray-700 rounded p-2 overflow-x-auto whitespace-pre-wrap dark:text-gray-100">
          {details ? JSON.stringify(details, null, 2) : "(none)"}
        </pre>
      ) : !details ? (
        <div className="text-gray-400 dark:text-gray-500">(no details)</div>
      ) : hasDiff ? (
        <DiffTable before={details.before} after={details.after} />
      ) : (
        <KeyValueTable obj={details} />
      )}
    </div>
  );
}

function fmt(v: any): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function KeyValueTable({ obj }: { obj: Record<string, any> }) {
  const entries = Object.entries(obj);
  if (entries.length === 0) {
    return <div className="text-gray-400 dark:text-gray-500">(empty)</div>;
  }
  return (
    <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded overflow-hidden">
      <table className="w-full text-xs">
        <tbody>
          {entries.map(([k, v]) => (
            <tr key={k} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
              <td className="px-2 py-1 text-gray-500 dark:text-gray-400 font-mono align-top w-40">{k}</td>
              <td className="px-2 py-1 break-all">{fmt(v)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DiffTable({
  before,
  after,
}: {
  before: Record<string, any>;
  after: Record<string, any>;
}) {
  // Union of keys — show all fields that appeared in either side. If a
  // key was added or removed, the missing side shows "—".
  const keys = Array.from(new Set([...Object.keys(before || {}), ...Object.keys(after || {})]));
  return (
    <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-gray-50 dark:bg-gray-900/50">
          <tr>
            <th className="px-2 py-1 text-left text-gray-500 dark:text-gray-400 font-medium w-32">Field</th>
            <th className="px-2 py-1 text-left text-gray-500 dark:text-gray-400 font-medium">Before</th>
            <th className="px-2 py-1 text-left text-gray-500 dark:text-gray-400 font-medium">After</th>
          </tr>
        </thead>
        <tbody>
          {keys.map((k) => {
            const b = before?.[k];
            const a = after?.[k];
            const changed = JSON.stringify(b) !== JSON.stringify(a);
            return (
              <tr key={k} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
                <td className="px-2 py-1 text-gray-500 dark:text-gray-400 font-mono align-top">{k}</td>
                <td
                  className={
                    "px-2 py-1 break-all " +
                    (changed ? "bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300" : "")
                  }
                >
                  {fmt(b)}
                </td>
                <td
                  className={
                    "px-2 py-1 break-all " +
                    (changed
                      ? "bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300"
                      : "")
                  }
                >
                  {fmt(a)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
